import { Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';

import { process } from '@progress/kendo-data-query';

import { PieSlice } from '../../interfaces';
import { EmptyStateMessage, ReportDefinition, User, Widget } from '../../models';
import { WIDGET_AGGREGATOR_CONSTANTS, WIDGET_CONSTANTS, WIDGET_OPERATOR_CONSTANTS } from '../../shared/constants';
import { FieldType, SiteFieldsNumericIds, TriggerOperator, WidgetType } from '../../shared/enums';
import { Aggregators } from './enums';
import { GridAggregation } from './enums/grid-aggregation.enum';
import { Field, Report, REPORT_SITE_DATA_SITE_STATUS, ReportSiteData } from './models';

@Injectable({
    providedIn: 'root'
})
export class WidgetLogicService {

    private readonly _reportBackedWidgetTypes = [WidgetType.Pie, WidgetType.Donut, WidgetType.Number, WidgetType.Report];

    doesWidgetUseReportData(widget: Widget): boolean {
        return !!widget.reportId && this._reportBackedWidgetTypes.includes(widget.type as WidgetType);
    }

    filterReportValuesByMetricRule(reportData: ReportSiteData[], columnSerializedId: string, triggerOperator: TriggerOperator, triggerValue: boolean | string, isStatusField: boolean, selectedField: Field, gridAggregation: GridAggregation | undefined): unknown[] | undefined {
        const isNotGridField = !selectedField.dataId;
        const rowValues = this.getRowValuesForMetricRule(reportData, columnSerializedId, isNotGridField, gridAggregation);

        switch (triggerOperator) {
            case TriggerOperator.Equal:
                return rowValues
                    .filter(value => (isStatusField ? this.convertStatusValue(value) : value) === triggerValue);
            case TriggerOperator.NotEqual:
                return rowValues
                    .filter(value => (isStatusField ? this.convertStatusValue(value) : value) !== triggerValue);
            case TriggerOperator.HasValue:
                if (selectedField?.FieldType?.id === FieldType.CheckBox) {
                    return rowValues
                        .filter(value => value);
                }
                else {
                    return rowValues
                        .filter(value => value !== null && value !== undefined && value !== '');
                }
            case TriggerOperator.NoValue:
                if (selectedField?.FieldType?.id === FieldType.CheckBox) {
                    return rowValues
                        .filter(value => !value);
                }
                else {
                    return rowValues
                        .filter(value => value === null || value === undefined || value === '');
                }
        }
    }

    getRowValuesForMetricRule(reportData: ReportSiteData[], columnSerializedId: string, isNotGridField: boolean, gridAggregation: GridAggregation | undefined): unknown[] {
        if (isNotGridField || gridAggregation === GridAggregation.PerSite) {
            return this.getDistinctValuesBySite(reportData, columnSerializedId);
        }

        return reportData.map((item: ReportSiteData) => item[columnSerializedId]);
    }

    convertStatusValue(value: unknown): boolean {
        return value as string === REPORT_SITE_DATA_SITE_STATUS.Active ? true : false;
    }

    calculateAggregatedNumberValue(aggregateType: Aggregators, values: unknown[]): number | undefined {
        switch (aggregateType) {
            case Aggregators.Count:
                return values.length;
            case Aggregators.Sum: {
                const numericValues = this.getNumericValues(values);
                return numericValues
                    ? numericValues.reduce((sum, value) => sum + value, 0)
                    : undefined;
            }
            case Aggregators.Average: {
                const numericValues = this.getNumericValues(values);
                return (numericValues?.length)
                    ? numericValues.reduce((sum, value) => sum + value, 0) / numericValues.length
                    : undefined;
            }
        }
    }

    getNumberWidgetTooltip(selectedField: Field, aggregateType: Aggregators, triggerOperator: TriggerOperator, triggerValue: boolean | string | undefined, gridAggregation: GridAggregation | undefined): string {
        let gridAggregationText = '';

        if (gridAggregation === GridAggregation.PerSite) {
            gridAggregationText = ' by Site';
        }

        if (gridAggregation === GridAggregation.PerRow) {
            gridAggregationText = ' by Row';
        }

        return `${WIDGET_AGGREGATOR_CONSTANTS.find(operators => operators.id === aggregateType)?.name} of ${selectedField?.Name}${gridAggregationText} | Logic applied: ${this.triggerOperatorText(selectedField, triggerOperator, triggerValue)}`;
    }

    // Previously saved grid-field widgets have no gridAggregation; bake in the per-row default once.
    normalizeGridAggregation(widget: Widget, selectedFields: Field[] | undefined): void {
        const rule = widget?.widgetMetricRules?.[0];

        if (!rule || rule.gridAggregation !== undefined) {
            return;
        }

        const selectedField = selectedFields?.find(field => field.id === widget.columnIds?.[0]);

        if (selectedField?.dataId !== undefined) {
            rule.gridAggregation = GridAggregation.PerRow;
        }
    }

    getWidgetSourceText(reportName: string, savedViewName?: string): string {
        return savedViewName
            ? `Report: ${reportName} - Saved View: ${savedViewName}`
            : `Report: ${reportName}`;
    }

    getAggregateWidgetSourceText(reportName: string, savedViewName?: string, field?: Field): string {
        return savedViewName
            ? `Report: ${reportName} - Saved View: ${savedViewName}${field ? ` | Column: ${field.Name}` : ''}`
            : `Report: ${reportName}${field ? ` | Column: ${field.Name}` : ''}`;
    }

    triggerOperatorText(selectedField: Field, triggerOperator: TriggerOperator, triggerValue: boolean | string | undefined): string {
        switch (selectedField?.id) {
            case SiteFieldsNumericIds.State:
                return (WIDGET_OPERATOR_CONSTANTS.SITE_STATE.find(operator => operator.id === triggerOperator)?.name ?? '') + ` ${triggerValue}`;
            case SiteFieldsNumericIds.Status:
                return (WIDGET_OPERATOR_CONSTANTS.SITE_STATUS.find(operator => operator.id === triggerOperator)?.name ?? '') + ` ${this.convertValueToStatus(triggerValue as boolean)}`;
        }

        switch (selectedField?.FieldType?.id) {
            case FieldType.CheckBox:
                return WIDGET_OPERATOR_CONSTANTS.CHECKBOX.find(operator => operator.id === triggerOperator)?.name ?? '';
            case FieldType.DropDown:
                return (WIDGET_OPERATOR_CONSTANTS.DROPDOWN.find(operator => operator.id === triggerOperator)?.name ?? '') + (triggerOperator !== TriggerOperator.NoValue && triggerOperator !== TriggerOperator.HasValue ? ' ' + triggerValue : '');
            default:
                return WIDGET_OPERATOR_CONSTANTS.NUMBER.find(operator => operator.id === triggerOperator)?.name ?? '';
        }
    }

    private convertValueToStatus(value: boolean): string {
        return value ? REPORT_SITE_DATA_SITE_STATUS.Active : REPORT_SITE_DATA_SITE_STATUS.Inactive;
    }

    private getNumericValues(values: unknown[]): number[] | undefined {
        if (!values.length) {
            return undefined;
        }

        const numericValues: number[] = [];

        for (const value of values) {
            if (typeof value !== 'number' || !Number.isFinite(value)) {
                return undefined;
            }

            numericValues.push(value);
        }

        return numericValues;
    }

    private getDistinctRowsBySite(reportData: ReportSiteData[]): ReportSiteData[] {
        const distinctRows: ReportSiteData[] = [];
        const seenRowKeys = new Set<string>();

        for (const row of reportData) {
            const rowKey = this.getDistinctRowKey(row);

            if (seenRowKeys.has(rowKey)) {
                continue;
            }

            seenRowKeys.add(rowKey);
            distinctRows.push(row);
        }

        return distinctRows;
    }

    private getDistinctValuesBySite(reportData: ReportSiteData[], columnSerializedId: string): unknown[] {
        const bestValueBySite = new Map<string, unknown>();

        for (const row of reportData) {
            const siteKey = this.getDistinctRowKey(row);
            const value = row[columnSerializedId];

            if (!bestValueBySite.has(siteKey)) {
                bestValueBySite.set(siteKey, value);
                continue;
            }

            const existingIsBlank = bestValueBySite.get(siteKey) === null || bestValueBySite.get(siteKey) === undefined || bestValueBySite.get(siteKey) === '';
            const newHasValue = value !== null && value !== undefined && value !== '';

            if (existingIsBlank && newHasValue) {
                bestValueBySite.set(siteKey, value);
            }
        }

        return Array.from(bestValueBySite.values());
    }

    private getDistinctRowKey(row: ReportSiteData): string {
        const siteScopedKey = row.SiteId;

        if (siteScopedKey === undefined || siteScopedKey === null || siteScopedKey === '') {
            return JSON.stringify(row);
        }

        return String(siteScopedKey);
    }

    applyReportView(rows: ReportSiteData[], report: Report, reportViewId: string | undefined): ReportSiteData[] {
        if (!reportViewId) {
            return rows;
        }
        const state = report.customViews?.find(v => v.id === reportViewId)?.kendoGridState;
        if (!state) {
            return rows;
        }
        return process(rows, state).data as ReportSiteData[];
    }

    aggregateSlices(rows: ReportSiteData[], categoryField: Field | undefined, users: User[]): PieSlice[] {
        const isNotGridField = !categoryField?.dataId;
        const sourceRows = isNotGridField ? this.getDistinctRowsBySite(rows) : rows;

        if (categoryField?.SelectOption === FieldType.Person) {
            return this.createPieSliceForPersonType(sourceRows, categoryField, users);
        }

        const key = categoryField?.serializedId;
        const counts = new Map<string, number>();

        for (const row of sourceRows) {
            const rawValue = key ? row[key] : undefined;
            const category = this.normalizeCategory(rawValue, categoryField);
            counts.set(category, (counts.get(category) ?? 0) + 1);
        }

        return Array.from(counts.entries()).map(([category, count]) => ({ category, count }));
    }

    private normalizeCategory(value: unknown, categoryField?: Field): string {
        if (categoryField?.SelectOption === FieldType.CheckBox) {
            return `${categoryField.Name} is ${value ? 'true' : 'false'}`;
        }

        if (value === null || value === undefined || value === '') {
            return WIDGET_CONSTANTS.EMPTY_CATEGORY_LABEL;
        }

        if (Array.isArray(value)) {
            return value.length ? value.join(', ') : WIDGET_CONSTANTS.EMPTY_CATEGORY_LABEL;
        }

        return String(value);
    }

    private createPieSliceForPersonType(rows: ReportSiteData[], categoryField: Field | undefined, users: User[]): PieSlice[] {
        const key = categoryField?.serializedId;
        if (!key) {
            return [];
        }

        const usersById = new Map(users.map(u => [u.id, u]));
        const countsById = new Map<string, { name: string; count: number; }>();

        for (const row of rows) {
            const rawValue = row[key];
            if (!rawValue?.length) {
                continue;
            }

            for (const uid of rawValue) {
                const user = usersById.get(uid);
                const id = user?.id;
                const name = user?.name;

                if (!id || !name) {
                    continue;
                }

                const existing = countsById.get(id);
                if (existing) {
                    existing.count++;
                }
                else {
                    countsById.set(id, { name, count: 1 });
                }
            }
        }

        return Array.from(countsById.values(), ({ name, count }) => ({ category: name, count }));
    }

    emptyStateMessage(): EmptyStateMessage {
        const widgetLoadErrorMessage = new EmptyStateMessage({
            header: WIDGET_CONSTANTS.NO_DATA_EMPTY_STATE_HEADER,
            bodyLinesInnerHtml: [WIDGET_CONSTANTS.NO_DATA_EMPTY_STATE_BODY],
        });

        return widgetLoadErrorMessage;
    }

    emptyStateLoadFailMessage(): EmptyStateMessage {
        const widgetLoadErrorMessage = new EmptyStateMessage({
            header: WIDGET_CONSTANTS.LOAD_ERROR_EMPTY_STATE_HEADER,
            bodyLinesInnerHtml: [WIDGET_CONSTANTS.LOAD_ERROR_EMPTY_STATE_BODY],
        });

        return widgetLoadErrorMessage;
    }

    autoSelectedFieldIds(form: FormGroup, reportDefinitions: ReportDefinition[] | undefined): Set<string> {
        const reportId = form.get('reportId')?.value as string;
        if (!reportId || !reportDefinitions) {
            return new Set<string>();
        }

        const definition = reportDefinitions?.find(d => d.id === reportId);
        return new Set(
            (definition?.autoSelectedFields ?? [])
                .map(f => f.id)
                .filter((id): id is string => !!id)
        );
    }

    validFieldIds(form: FormGroup, reportDefinitions: ReportDefinition[] | undefined): Set<string> {
        const reportId = form.get('reportId')?.value as string;
        if (!reportId || !reportDefinitions) {
            return new Set<string>();
        }

        const definitions = reportDefinitions?.find(d => d.id === reportId);
        if (!definitions) {
            return new Set<string>();
        }

        const autoIds = (definitions.autoSelectedFields ?? [])
            .map(field => field.id)
            .filter((id): id is string => !!id);

        const selectedIds = (definitions.selectedFields ?? [])
            .map((field: { id?: string; }) => field.id)
            .filter((id): id is string => !!id);

        return new Set([...autoIds, ...selectedIds]);
    }
}
